import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { AlertTriangle, Settings, Download, Loader2, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { value: 'LAST_7_DAYS', label: '7 derniers jours' },
  { value: 'LAST_30_DAYS', label: '30 derniers jours' },
  { value: 'THIS_MONTH', label: 'Ce mois' },
  { value: 'LAST_MONTH', label: 'Mois dernier' },
  { value: 'LAST_90_DAYS', label: '90 derniers jours' },
];

function periodToDates(period) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const sub = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (period) {
    case 'LAST_7_DAYS': return [fmt(sub(7)), fmt(sub(1))];
    case 'LAST_30_DAYS': return [fmt(sub(30)), fmt(sub(1))];
    case 'LAST_90_DAYS': return [fmt(sub(90)), fmt(sub(1))];
    case 'THIS_MONTH': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return [fmt(start), fmt(today)];
    }
    case 'LAST_MONTH': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return [fmt(start), fmt(end)];
    }
    default: return [fmt(sub(30)), fmt(sub(1))];
  }
}

const formatEUR = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const formatNum = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const formatPct = (n) => `${((n || 0) * 100).toFixed(2)}%`;

export default function SEACampaigns() {
  const [period, setPeriod] = useState('LAST_30_DAYS');
  const [config, setConfig] = useState(null);
  const [seaClients, setSeaClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [tab, setTab] = useState('campaigns');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [start, end] = useMemo(() => periodToDates(period), [period]);

  useEffect(() => {
    (async () => {
      try {
        const [cfg, cls] = await Promise.all([
          axios.get(`${API}/google-ads/config`, { withCredentials: true }),
          axios.get(`${API}/clients`, { withCredentials: true }),
        ]);
        setConfig(cfg.data);
        const linked = (cls.data || []).filter(c => c.google_ads_customer_id);
        setSeaClients(linked);
        if (linked.length > 0 && !selectedClient) setSelectedClient(linked[0].id);
      } catch (e) {
        setConfig({ is_connected: false });
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedClient || !config?.is_connected) return;
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ client_id: selectedClient, start, end });
        const [camp, kw] = await Promise.all([
          axios.get(`${API}/google-ads/campaigns?${params}`, { withCredentials: true }),
          axios.get(`${API}/google-ads/keywords?${params}&limit=300`, { withCredentials: true }),
        ]);
        if (cancel) return;
        setCampaigns(camp.data.campaigns || []);
        setKeywords(kw.data.keywords || []);
      } catch (e) {
        if (!cancel) toast.error(e.response?.data?.detail || 'Erreur lors du chargement des campagnes');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [selectedClient, start, end, config?.is_connected]);

  const exportCsv = (rows, columns, filename) => {
    if (!rows.length) return;
    const header = columns.map(c => c.label).join(';');
    const lines = rows.map(r => columns.map(c => {
      const val = c.get(r);
      const str = String(val ?? '');
      return /[;\n"]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(';'));
    const csv = '\uFEFF' + [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const filteredCampaigns = campaigns.filter(c => !search || c.campaign_name.toLowerCase().includes(search.toLowerCase()));
  const filteredKeywords = keywords.filter(k => !search || k.keyword.toLowerCase().includes(search.toLowerCase()) || (k.campaign_name || '').toLowerCase().includes(search.toLowerCase()));

  if (!config) {
    return <Layout><div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div></Layout>;
  }

  if (!config.is_connected) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Campagnes Google Ads</h1>
            <p className="text-neutral-500 mt-1">Performance détaillée par campagne et mot-clé</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <div className="flex-1">
                <h2 className="font-heading font-semibold text-lg text-neutral-900">Google Ads non connecté</h2>
                <Link to="/settings"><Button className="bg-orange-600 hover:bg-orange-700 mt-3"><Settings className="w-4 h-4 mr-1" /> Configurer Google Ads</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (seaClients.length === 0) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Campagnes Google Ads</h1>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-5 text-sm text-blue-900">
            Aucun client n'est lié à un compte Google Ads. Allez dans le <Link to="/sea/dashboard" className="underline font-medium">Tableau de bord SEA</Link> et cliquez sur <em>Lier comptes</em>.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="sea-campaigns">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Campagnes Google Ads</h1>
            <p className="text-neutral-500 mt-1">Performance détaillée par campagne et mot-clé</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-56" data-testid="sea-camp-client-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {seaClients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tabs + search */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200">
          <div className="flex">
            <button
              onClick={() => setTab('campaigns')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'campaigns' ? 'border-orange-600 text-orange-700' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}
              data-testid="sea-tab-campaigns"
            >
              Campagnes ({campaigns.length})
            </button>
            <button
              onClick={() => setTab('keywords')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'keywords' ? 'border-orange-600 text-orange-700' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}
              data-testid="sea-tab-keywords"
            >
              Mots-clés ({keywords.length})
            </button>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="pl-7 w-64 h-8 text-sm"
                data-testid="sea-search-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (tab === 'campaigns') {
                  exportCsv(filteredCampaigns, [
                    { label: 'Campagne', get: r => r.campaign_name },
                    { label: 'Statut', get: r => r.status },
                    { label: 'Type', get: r => r.channel_type },
                    { label: 'Impressions', get: r => r.impressions },
                    { label: 'Clics', get: r => r.clicks },
                    { label: 'CTR', get: r => formatPct(r.ctr) },
                    { label: 'CPC moy.', get: r => r.average_cpc.toFixed(2) },
                    { label: 'Coût', get: r => r.cost.toFixed(2) },
                    { label: 'Conversions', get: r => r.conversions },
                    { label: 'CPA', get: r => r.cost_per_conversion.toFixed(2) },
                  ], `gads_campagnes_${selectedClient}_${start}_${end}.csv`);
                } else {
                  exportCsv(filteredKeywords, [
                    { label: 'Mot-clé', get: r => r.keyword },
                    { label: 'Match', get: r => r.match_type },
                    { label: 'QS', get: r => r.quality_score ?? '' },
                    { label: 'Campagne', get: r => r.campaign_name },
                    { label: 'AdGroup', get: r => r.ad_group_name },
                    { label: 'Impressions', get: r => r.impressions },
                    { label: 'Clics', get: r => r.clicks },
                    { label: 'CTR', get: r => formatPct(r.ctr) },
                    { label: 'CPC moy.', get: r => r.average_cpc.toFixed(2) },
                    { label: 'Coût', get: r => r.cost.toFixed(2) },
                    { label: 'Conversions', get: r => r.conversions },
                  ], `gads_motscles_${selectedClient}_${start}_${end}.csv`);
                }
              }}
              data-testid="sea-export-csv-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
        ) : tab === 'campaigns' ? (
          <CampaignsTable rows={filteredCampaigns} />
        ) : (
          <KeywordsTable rows={filteredKeywords} />
        )}
      </div>
    </Layout>
  );
}

function CampaignsTable({ rows }) {
  if (!rows.length) return <div className="text-sm text-neutral-500 py-8 text-center">Aucune campagne trouvée pour cette période.</div>;
  return (
    <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 border-b border-neutral-200">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-neutral-600">Campagne</th>
            <th className="text-left px-3 py-2 font-medium text-neutral-600">Statut</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Impr.</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Clics</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">CTR</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">CPC</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Coût</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Conv.</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">CPA</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.campaign_id} className="border-b border-neutral-50 hover:bg-neutral-50">
              <td className="px-3 py-2">
                <div className="font-medium">{r.campaign_name}</div>
                <div className="text-[11px] text-neutral-400">{r.channel_type}</div>
              </td>
              <td className="px-3 py-2">
                <Badge variant={r.status === 'ENABLED' ? 'default' : 'outline'} className="text-[10px]">
                  {r.status}
                </Badge>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{formatNum(r.impressions)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatNum(r.clicks)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{formatPct(r.ctr)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{formatEUR(r.average_cpc)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEUR(r.cost)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.conversions.toFixed(1)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{r.conversions ? formatEUR(r.cost_per_conversion) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KeywordsTable({ rows }) {
  if (!rows.length) return <div className="text-sm text-neutral-500 py-8 text-center">Aucun mot-clé trouvé pour cette période.</div>;
  return (
    <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 border-b border-neutral-200">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-neutral-600">Mot-clé</th>
            <th className="text-left px-3 py-2 font-medium text-neutral-600">Match</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">QS</th>
            <th className="text-left px-3 py-2 font-medium text-neutral-600">Campagne</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Impr.</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Clics</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">CTR</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Coût</th>
            <th className="text-right px-3 py-2 font-medium text-neutral-600">Conv.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.keyword}-${i}`} className="border-b border-neutral-50 hover:bg-neutral-50">
              <td className="px-3 py-2 font-medium">{r.keyword}</td>
              <td className="px-3 py-2 text-xs text-neutral-500">{r.match_type}</td>
              <td className="px-3 py-2 text-right">
                {r.quality_score ? (
                  <Badge className={`text-[10px] ${r.quality_score >= 7 ? 'bg-green-100 text-green-800' : r.quality_score >= 4 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{r.quality_score}/10</Badge>
                ) : (
                  <span className="text-neutral-300">—</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-neutral-600">
                <div>{r.campaign_name}</div>
                <div className="text-[10px] text-neutral-400">{r.ad_group_name}</div>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{formatNum(r.impressions)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatNum(r.clicks)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{formatPct(r.ctr)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEUR(r.cost)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.conversions.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
